import axios from 'axios';
import _get from 'lodash/get';
import _set from 'lodash/set';
import _isEmpty from 'lodash/isEmpty';
import _cloneDeep from 'lodash/cloneDeep';
import store from '@/store';
import { post } from '@/lib/utils';
import { primaryKeys as primary_keys, sequencedKeys as sequenced_keys } from '@/lib/vuexUtils';
import { filterDbProperties, determineRInputType } from '@/lib/metaUtils';
import { UQL_OPS } from '@/lib/constants';
import { FatalErrorException } from '@/lib/exceptions';


const getSchemaData = () => post('/tg/meta/describe');
const getErrors = () => axios.get('/tg/res/errors.txt');
const getLabels = () => axios.get('/tg/res/labels.txt');

const getClientSetupData = async () => {
  const { results, notification } = await post('/tg/system/get_client_setup_data', {
    dataproviders: true,
    sites: true,
  });
  const setupData = _get(results, '[0]', []);
  let payload = null;
  if (notification && notification[0] === '{VersionError}') {
    // If there's a version mismatch, we need to have the user refresh the page.
    payload = {
      title: 'Version Error',
      text: 'Your browser\'s version of Loft does not match the current version. Please refresh your browser.',
      bool: true,
    };
  }
  else if (!Object.keys(setupData).length) {
    // If no keys are present on the return, this means
    // something went wrong with retrieval of client setup data.
    payload = {
      errorMessage: 'Client setup data failed to load, please contact your system administrator.',
      bool: true,
    };
  }
  if (payload) {
    store.dispatch('notify/toggleFatalErrorModal', payload);
    return {};
  }
  const visiblesites = { clsName: 'Site' };
  _set(setupData, 'dataproviders.visiblesites', visiblesites);
  return setupData;
};

const getMenuTreeData = async () => {
  const { results } = await post('/tg/system/get_menu_tree');
  return results;
};

const mapSystemParameters = (params) => {
  const mappedSysParams = {};
  for (const param of params) {
    const { name, type, stringval, numberval, dateval } = param;
    let value;
    switch (type) {
      case 's':
        value = stringval;
        break;
      case 'n':
        value = numberval;
        break;
      case 'd':
        value = dateval;
        break;
      default:
        throw new FatalErrorException(
          `The system parameter [${name}] has an invalid type code of [${type}]. \
          The system may not perform properly until this is fixed. \
          Please notify your system administrator!`,
        );
    }
    mappedSysParams[name] = value;
  }
  return mappedSysParams;
};

const localState = {
  // camelCase
  schema: {
    // Site and CashDrawer are referenced before meta are loaded!
    Site: { data_value_field: 'siteid' },
    CashDrawer: { data_value_field: 'cashdrawerid' },
  },
  clientSetupData: null,
  ops: Object.values(UQL_OPS),
  operatorLabels: {},
};


const localGetters = {
  // underscore_lowercase
  primary_keys,

  sequenced_keys,

  meta: state => state.schema,
  clientSetupData: state => state.clientSetupData,
  databaseName: state => _get(state, 'clientSetupData.database_name', ''),

  data_value_field: state => className => _get(state, `schema.${className}.data_value_field`),

  singular_label: state => className => {
    const meta = _get(state, `schema.${className}`);
    return _get(meta, 'labels.singular', className);
  },

  plural_label: state => className => {
    const meta = _get(state, `schema.${className}`);
    return _get(meta, 'labels.plural', className);
  },

  /**
   * @param {string} className
   * @param {Object} [localSchema] - used to override the state schema if necessary
   */
  class_schema: state => (className, localSchema) => {
    const schema = _get(localSchema, className) || _get(state, `schema.${className}`);
    return schema || {};
  },

  db_properties: state => (class_name, localSchema, options = {}, source_string_array = ['db_schema', 'pseudo_column']) => {
    const schema = (!_isEmpty(localSchema) && localSchema) || _get(state, `schema.${class_name}`);
    if (!schema) return {};
    return filterDbProperties(schema, source_string_array, options);
  },

  mapper_relationships: state => (class_name, options = {}) => {
    if (!state.schema[class_name]) return {};
    return filterDbProperties(state.schema[class_name], ['mapper_relationship'], options);
  },

  gers_v12: state => {
    const gersVersion = _get(state, 'clientSetupData.client_config.gers_version', '');
    return gersVersion >= '12.0';
  },

  /**
   * @param {string} className
   * @param {string} fieldName
   * @param {Object} [localSchema] - used to override the state schema if necessary
   */
  property: state => (className, fieldName, localSchema) => {
    const schema = _get(localSchema, className) || (localSchema && localSchema.properties ? localSchema : null) || _get(state, `schema.${className}`);
    const props = _get(schema, 'properties');
    return _get(props, fieldName, null);
  },

  property_type: (state, getters) => (className, fieldName, localSchema = {}) => {
    const prop = getters.property(className, fieldName, localSchema) || {};
    if (!prop.property_meta) return '';
    return determineRInputType(prop, className, fieldName);
  },

  /**
   * @param {string} className
   * @param {string} fieldName
   * @param {Object} [localSchema] - used to override the state schema if necessary
   */
  property_label: state => (className, fieldName, localSchema) => {
    const schema = _get(localSchema, className) || _get(state, `schema.${className}`);
    return _get(schema, `properties.${fieldName}.labels.label`, null);
  },

  class_label: state => (className, plural = false) => {
    const labelType = plural ? 'plural' : 'singular';
    return _get(state, `schema[${className}].labels.${labelType}`, className);
  },

  field_attributes: state => (model, isPersisted, localSchema = null, displayFields = []) => {
    const getDisplayFields = (meta, dspFlds) => {
      if (dspFlds && dspFlds.length) return dspFlds;
      const display = _get(meta, 'display', []);
      return display.reduce((fields, { displayfields }) => {
        fields.push(...displayfields);
        return fields;
      }, []);
    };
    const schema = localSchema || state.schema[model._classname];
    const modelKeys = Object.keys(model);
    const uniqueFields = new Set([...modelKeys, ...getDisplayFields(schema, displayFields)]);
    return [...uniqueFields].reduce((acc, key) => {
      if (!Array.isArray(model[key]) && !(model[key] instanceof Object)) {
        const propertySchema = _get(schema, `properties.${key}`);
        if (!propertySchema) return acc;
        let excludeFrom = _get(propertySchema, 'property_meta.excludefrom');
        if (!excludeFrom) {
          acc[key] = model[key];
          return acc;
        }
        excludeFrom = excludeFrom.split(',');
        const isNotNew = !isPersisted && !excludeFrom.includes('new');
        const isNotEdit = isPersisted && !excludeFrom.includes('edit');
        if (isNotNew || isNotEdit) acc[key] = model[key];
      }
      return acc;
    }, {});
  },

  associations: state => (className) => state.schema[className].eagerloading,

  dataProviderObject: (state, getters) => ({ className, fieldName, dataProvider, localSchema = null }) => {
    if (localSchema && !dataProvider) {
      dataProvider = _get(localSchema[className], 'property_meta.dataprovider');
    }
    if (!dataProvider && !localSchema) {
      dataProvider = _get(getters.property(className, fieldName), 'property_meta.dataprovider');
    }
    const dataProviders = _get(getters, 'clientSetupData.dataproviders');
    if (!dataProviders) return null;
    return dataProvider ? dataProviders[dataProvider.split(':').slice(-1).join()] : null;
  },

  // TEST: needs a test case
  systemParameter: state => parameterName => {
    const sysParams = _get(state, 'clientSetupData.systemparameters');
    if (!sysParams) return null;
    return _get(sysParams, parameterName);
  },

  operatorLabels: state => state.operatorLabels,

  enableMaps: state => {
    const { enablemaps, googlemaps_apikey } = _get(state, 'clientSetupData.client_config');
    return enablemaps === 'true' || !!googlemaps_apikey;
  },

  googleMapsApiKey: state => _get(state, 'clientSetupData.client_config.googlemaps_apikey'),

  ssoEnabled: (state) => {
    const value = _get(state, 'clientSetupData.client_config.sso');
    // cast to Boolean like asbool() does in the server:
    return (['1', 'true', 't', 'on', 'yes', 'y'].includes(value));
  },

};


const localActions = {
  // camelCase
  async getClientSetupData({ commit }) {
    const clientSetupData = await getClientSetupData();
    const { systemparameters } = clientSetupData;
    const mappedSysParams = mapSystemParameters(systemparameters);
    commit('update_client_setup_data', { ...clientSetupData, systemparameters: mappedSysParams });
    commit('auth/loginSites', clientSetupData.sites, { root: true });
  },

  async clearClientSetupData({ commit, dispatch }) {
    dispatch('auth/logout', { shouldDisassociate: false }, { root: true });
    await dispatch('auth/resetAuth', null, { root: true });
    commit('update_client_setup_data', null);
    dispatch('cached/initializeCached', null, { root: true });
  },

  async getMetadata({ commit, state, dispatch }) {
    await axios.all([getSchemaData(), getMenuTreeData(), getErrors(), getLabels()])
      .then(axios.spread((schema_data, menu_tree_data, error_data, label_data) => {
        commit('update_schema', schema_data.results[0]);
        dispatch('menu/initializeMenuData', menu_tree_data, { root: true });
        dispatch('translations/initializeTranslations', {
          errors: _get(error_data, 'data', ''),
          labels: _get(label_data, 'data', ''),
        }, { root: true });
        dispatch('cached/initializeCached', state.clientSetupData, { root: true });
      }));
  },

  initializeUqlConstants({ rootGetters, state, commit }) {
    const opObjs = state.ops.reduce((accObj, op) => {
      accObj[op] = rootGetters['translations/label'](`{UQL.${op}}l`);
      return accObj;
    }, {});
    commit('set_uql_constants', opObjs);
  },
};


const localMutations = {
  // underscore_case
  update_schema(state, newSchema) {
    const user = _cloneDeep(newSchema.User);
    state.schema = { ...newSchema, UserAlias: { ...user, data_value_field: 'useralias', classname: 'UserAlias' } };
  },

  update_client_setup_data(state, clientSetupData) {
    state.clientSetupData = clientSetupData;
  },

  set_uql_constants(state, labels) {
    state.operatorLabels = labels;
  },
};


export default {
  namespaced: true,
  state: localState,
  getters: localGetters,
  actions: localActions,
  mutations: localMutations,
};
